import { create } from 'zustand';

import { ANNOTATION_BOX_FILTER } from '@utils/constants';

/**
 * State of an object annotation session (Collection view): what the backend
 * stored for the object (loaded from its list and refreshed after each save)
 * plus the open tile's pending corrections. Survives the annotator's unmount so a
 * session can switch object, or exit and re-enter, without losing saved boxes.
 *
 * - `savedByNode`: `{ [nodeUuid]: { [tileName]: Feature[] } }` — clones of the
 *   human boxes saved for an object in a tile (an empty list is an empty mark).
 *   The map shows its own live copies. Model boxes are server state, kept out.
 * - `tilesByNode`: the grid tiles inside the session boundary per object, so a
 *   switch back does not fetch again.
 * - `dirty`: the open tile differs from what was saved for the current object.
 * - `edited`: the open tile holds box changes the user made (`dirty` without a
 *   never-saved tile), which locks the box filter until they are saved or discarded.
 * - `boxFilter`: which boxes the map draws (an `ANNOTATION_BOX_FILTER` value).
 * - `boxesVersion`: bumped whenever the boxes on the map change, so views that read
 *   the live boxes (the labels card) render again.
 * - `stickyLabelByNode`: the label new boxes of an object get (null = default).
 * - `labelsByNode`: labels the user added per object (boxes may carry more).
 * - `selectedBoxId`: the box being moved or reshaped, which the chips relabel.
 * - `statusByNode`: the run status the list reported (`not_run` / `current` / `outdated`).
 * - `objectSwitchGuard`: set by the annotator; the object tree awaits it before
 *   switching object (false: stay). Null when no session runs.
 * - `pendingRejects`: `{ [nodeUuid]: { [tileName]: Feature[] } }` — model boxes the
 *   user erased in a tile, sent as `reject` rows by the tile's next save.
 */
const initialState = {
	savedByNode: {},
	tilesByNode: {},
	dirty: false,
	edited: false,
	boxFilter: ANNOTATION_BOX_FILTER.ALL,
	boxesVersion: 0,
	stickyLabelByNode: {},
	labelsByNode: {},
	selectedBoxId: null,
	statusByNode: {},
	pendingRejects: {},
	objectSwitchGuard: null,
};

export const useAnnotationSessionStore = create((set, get) => ({
	...initialState,

	saveTile: (nodeUuid, tileName, features) =>
		set(state => ({
			savedByNode: {
				...state.savedByNode,
				[nodeUuid]: { ...(state.savedByNode[nodeUuid] ?? {}), [tileName]: features },
			},
		})),

	/** Replace what is stored for an object: `byTile` is `{ [tileName]: Feature[] }`. */
	replaceNode: (nodeUuid, byTile, status) =>
		set(state => ({
			savedByNode: { ...state.savedByNode, [nodeUuid]: byTile },
			statusByNode:
				status === undefined
					? state.statusByNode
					: { ...state.statusByNode, [nodeUuid]: status },
		})),

	/** Remember a model box the user erased in a tile. */
	addReject: (nodeUuid, tileName, feature) =>
		set(state => {
			const byTile = state.pendingRejects[nodeUuid] ?? {};
			return {
				pendingRejects: {
					...state.pendingRejects,
					[nodeUuid]: { ...byTile, [tileName]: [...(byTile[tileName] ?? []), feature] },
				},
			};
		}),

	/** Forget the pending rejects of an object in a tile (after its save). */
	clearRejects: (nodeUuid, tileName) =>
		set(state => {
			const { [tileName]: _gone, ...rest } = state.pendingRejects[nodeUuid] ?? {};
			return { pendingRejects: { ...state.pendingRejects, [nodeUuid]: rest } };
		}),

	/** Drop one pending reject (an erased model box that was brought back). */
	clearRejectFor: (nodeUuid, tileName, boxId) =>
		set(state => {
			const byTile = state.pendingRejects[nodeUuid] ?? {};
			const rest = (byTile[tileName] ?? []).filter(box => box.get('boxId') !== boxId);
			const { [tileName]: _gone, ...others } = byTile;
			return {
				pendingRejects: {
					...state.pendingRejects,
					[nodeUuid]: rest.length ? { ...others, [tileName]: rest } : others,
				},
			};
		}),

	/** The pending rejects of an object in a tile. */
	rejectsInTile: (nodeUuid, tileName) => get().pendingRejects[nodeUuid]?.[tileName] ?? [],

	/** Every saved box of an object, across its tiles. */
	savedFor: nodeUuid => Object.values(get().savedByNode[nodeUuid] ?? {}).flat(),

	/** Saved boxes of an object in one tile. */
	savedInTile: (nodeUuid, tileName) => get().savedByNode[nodeUuid]?.[tileName] ?? [],

	/** Every saved box of every object: the whole session, for when the map is rebuilt. */
	savedAll: () =>
		Object.values(get().savedByNode).flatMap(byTile => Object.values(byTile).flat()),

	/** Saved boxes of every object in one tile (an image, for single-image projects). */
	savedInTileAll: tileName =>
		Object.values(get().savedByNode).flatMap(byTile => byTile[tileName] ?? []),

	setTiles: (nodeUuid, tiles) =>
		set(state => ({ tilesByNode: { ...state.tilesByNode, [nodeUuid]: tiles } })),

	setDirty: dirty => set({ dirty }),
	setEdited: edited => set({ edited }),
	setBoxFilter: boxFilter => set({ boxFilter }),
	bumpBoxes: () => set(state => ({ boxesVersion: state.boxesVersion + 1 })),
	setObjectSwitchGuard: objectSwitchGuard => set({ objectSwitchGuard }),
	setSelectedBox: selectedBoxId => set({ selectedBoxId }),

	setStickyLabel: (nodeUuid, label) =>
		set(state => ({
			stickyLabelByNode: { ...state.stickyLabelByNode, [nodeUuid]: label ?? null },
		})),

	/** Remember a label for an object. Returns the normalised label, or null for a blank one. */
	addLabel: (nodeUuid, label) => {
		const clean = String(label ?? '')
			.trim()
			.toLowerCase();
		if (!clean) return null;
		set(state => {
			const known = state.labelsByNode[nodeUuid] ?? [];
			if (known.includes(clean)) return state;
			return { labelsByNode: { ...state.labelsByNode, [nodeUuid]: [...known, clean] } };
		});
		return clean;
	},

	reset: () => set(initialState),
}));
